import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupLayout from "../SetupLayout/SetupLayout";
import { useSetupDispatch, setClientInfo, resetSetup } from "../../../auth/SetupContext";
import { API_BASE_URL, CLIENT_ID, REDIRECT_URI } from "../../../auth/config";
import formStyles from "../setupForm.module.scss";

export default function CreateClient() {
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();

  const [clientId, setClientId] = useState(CLIENT_ID);
  const [redirectUri, setRedirectUri] = useState(REDIRECT_URI);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case "clientId":
        return value.trim() ? "" : "Client ID is required.";
      case "redirectUri":
        return value.trim() ? "" : "Redirect URI is required.";
      default:
        return "";
    }
  };

  const isFormValid =
    !validateField("clientId", clientId) && !validateField("redirectUri", redirectUri);

  const handleClientIdChange = (e) => {
    const { value } = e.target;
    setClientId(value);
    setFieldErrors((prev) => ({ ...prev, clientId: validateField("clientId", value) }));
  };

  const handleRedirectUriChange = (e) => {
    const { value } = e.target;
    setRedirectUri(value);
    setFieldErrors((prev) => ({ ...prev, redirectUri: validateField("redirectUri", value) }));
  };

  const handleBack = () => {
    navigate("/", { replace: true });
    setTimeout(() => dispatch(resetSetup()), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const nextFieldErrors = {
      clientId: validateField("clientId", clientId),
      redirectUri: validateField("redirectUri", redirectUri),
    };
    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_BASE_URL}/api/oauth/client`, { clientId, redirectUri });
      dispatch(setClientInfo(clientId, redirectUri));
      navigate("/setup/company", { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (typeof err.response?.data === "string" ? err.response.data : "") ||
          "Failed to register OAuth client"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SetupLayout
      step={1}
      totalSteps={6}
      title="Register OAuth client"
      subtitle="This app isn't registered with the SSO server yet. Let's set it up."
      error={error}
      onBack={handleBack}
      backDisabled={isSubmitting}
      loading={isSubmitting}
      loadingText="Registering client..."
    >
      <form onSubmit={handleSubmit} className={formStyles.body}>
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} htmlFor="clientId">Client ID</label>
          <input
            id="clientId"
            className={formStyles.input}
            value={clientId}
            onChange={handleClientIdChange}
            required
          />
          {fieldErrors.clientId && <span className={formStyles.fieldError}>{fieldErrors.clientId}</span>}
        </div>
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} htmlFor="redirectUri">Redirect URI</label>
          <input
            id="redirectUri"
            className={formStyles.input}
            value={redirectUri}
            onChange={handleRedirectUriChange}
            required
          />
          {fieldErrors.redirectUri && <span className={formStyles.fieldError}>{fieldErrors.redirectUri}</span>}
        </div>
        <div className={formStyles.btnRow}>
          <button type="submit" className={formStyles.primaryBtn} disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? "Registering..." : "Register client"}
          </button>
        </div>
      </form>
    </SetupLayout>
  );
}
